import { Link, NavLink, useNavigate } from "react-router-dom";
import { HeartPulse, User, LayoutDashboard, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth, homeFor } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";

export const Logo = ({ to = "/" }) => (
  <Link to={to} data-testid="nav-logo-link" className="flex items-center gap-2 group">
    <span className="h-9 w-9 rounded-xl bg-primary text-primary-foreground flex items-center justify-center transition-transform duration-300 group-hover:rotate-6">
      <HeartPulse className="h-5 w-5" />
    </span>
    <span className="font-display font-extrabold text-lg tracking-tight">
      ClinicsPro<span className="text-accent">.app</span>
    </span>
  </Link>
);

const navCls = ({ isActive }) =>
  `text-sm font-medium transition-colors hover:text-primary ${isActive ? "text-primary" : "text-slate-600"}`;

export const Header = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  return (
    <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border/70">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <div className="flex items-center gap-8">
          <Logo />
          <nav className="hidden md:flex items-center gap-6">
            <NavLink to="/clinics" className={navCls} data-testid="nav-browse-link">Browse clinics</NavLink>
            <NavLink to="/for-clinics" className={navCls} data-testid="nav-for-clinics-link">For clinics</NavLink>
            <NavLink to="/tour" className={navCls} data-testid="nav-tour-link">Product tour</NavLink>
            <NavLink to="/pricing" className={navCls} data-testid="nav-pricing-link">Pricing</NavLink>
          </nav>
        </div>
        <div className="flex items-center gap-2">
          {user ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => nav(homeFor(user))}
                data-testid={user.role === "patient" ? "nav-patient-portal-button" : "nav-admin-dashboard-button"}>
                {user.role === "patient" ? <User className="h-4 w-4 mr-1.5" /> : <LayoutDashboard className="h-4 w-4 mr-1.5" />}
                {user.role === "patient" ? "My bookings" : "Dashboard"}
              </Button>
              <Button variant="ghost" size="icon" onClick={async () => { await logout(); nav("/"); }} data-testid="nav-logout-button" aria-label="Log out">
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" asChild data-testid="nav-login-button"><Link to="/login">Log in</Link></Button>
              <Button size="sm" className="rounded-full px-5" asChild data-testid="nav-signup-button"><Link to="/signup">Sign up</Link></Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export const Footer = () => {
  const { home } = useContent();
  return (
  <footer className="border-t border-border mt-24 bg-[#1E3A34] text-slate-200">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid gap-10 md:grid-cols-4">
      <div className="md:col-span-2 space-y-3">
        <p className="font-display font-extrabold text-xl text-white">ClinicsPro<span className="text-accent">.app</span></p>
        <p className="text-sm text-slate-300 max-w-sm" data-testid="footer-tagline">{home.footer_tagline}</p>
      </div>
      <div className="space-y-2 text-sm">
        <p className="font-semibold text-white">Patients</p>
        <Link to="/clinics" className="block hover:text-white" data-testid="footer-browse-link">Browse clinics</Link>
        <Link to="/account" className="block hover:text-white" data-testid="footer-account-link">My bookings</Link>
      </div>
      <div className="space-y-2 text-sm">
        <p className="font-semibold text-white">Clinics</p>
        <Link to="/for-clinics" className="block hover:text-white" data-testid="footer-for-clinics-link">Why ClinicsPro</Link>
        <Link to="/tour" className="block hover:text-white" data-testid="footer-tour-link">Product tour</Link>
        <Link to="/pricing" className="block hover:text-white" data-testid="footer-pricing-link">Pricing</Link>
        <Link to="/for-clinics/signup" className="block hover:text-white" data-testid="footer-list-clinic-link">List your clinic</Link>
      </div>
    </div>
    <div className="border-t border-white/10 py-5 text-center text-xs text-slate-400">© {new Date().getFullYear()} ClinicsPro.app</div>
  </footer>
  );
};

export const SiteLayout = ({ children }) => (
  <div className="min-h-screen flex flex-col">
    <Header />
    <main className="flex-1">{children}</main>
    <Footer />
  </div>
);
